import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Fingerprint, X, ShieldCheck } from "lucide-react";
import { Btn } from "./ui";
import { useI18n } from "../lib/i18n";

export default function StewardModal({ target, onClose, onSign, loading }) {
  const { t } = useI18n();
  const [pin, setPin] = useState("");
  useEffect(() => {
    setPin("");
  }, [target]);

  const digestPayload = target
    ? {
        request_id: target.req_id,
        action_type: target.action_type,
        risk_level: target.risk_level,
        action_intent_digest: target.digest,
        assertion: "WebAuthn / FIDO2 public-key required",
      }
    : {};

  return (
    <AnimatePresence>
      {target && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-center glass px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="steward-modal"
        >
          <motion.div
            initial={{ scale: 0.94, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="w-full max-w-lg rounded-2xl border border-aegis-amber/30 bg-panel shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-edge px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-lg border border-aegis-amber/40 bg-aegis-amber/10 text-aegis-amber">
                  <Fingerprint size={18} />
                </span>
                <div>
                  <h3 className="font-heading text-[15px] font-medium text-slate-100">{t("steward.title")}</h3>
                  <p className="text-[11px] text-slate-400">{t("steward.sub")}</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="steward-close-btn" className="text-slate-400 transition-colors hover:text-slate-100">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-edge raise px-3 py-2">
                  <p className="text-[10px] uppercase tracking-overline text-slate-400">{t("steward.request")}</p>
                  <p className="font-mono text-sm text-slate-100">{target.req_id}</p>
                </div>
                <div className="rounded-lg border border-edge raise px-3 py-2">
                  <p className="text-[10px] uppercase tracking-overline text-slate-400">{t("steward.action")}</p>
                  <p className="truncate font-mono text-sm text-slate-100">{target.action_type}</p>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[10px] uppercase tracking-overline text-slate-400">action_intent_digest</p>
                <pre className="no-scrollbar overflow-x-auto rounded-lg border border-edge well p-3 font-mono text-[11px] leading-relaxed text-aegis-cyan">
{JSON.stringify(digestPayload, null, 2)}
                </pre>
              </div>

              {target.analysis && (
                <div>
                  <p className="mb-1.5 text-[10px] uppercase tracking-overline text-aegis-purple">
                    AEGIS forensic finding — human review required
                  </p>
                  <div className="no-scrollbar max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-aegis-purple/30 bg-aegis-purple/5 p-3 text-[12px] leading-relaxed text-slate-300">
                    {target.analysis}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-overline text-slate-400">{t("steward.pin")}</label>
                <input
                  data-testid="steward-pin-input"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && pin && onSign(pin)}
                  placeholder="• • • • • •"
                  autoFocus
                  className="mt-1.5 w-full rounded-lg border border-edge well px-3 py-2.5 font-mono tracking-[0.3em] text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-edge px-6 py-4">
              <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                <ShieldCheck size={13} className="text-aegis-emerald" /> {t("steward.anchor")}
              </span>
              <div className="flex gap-2">
                <Btn variant="ghost" onClick={onClose} testid="steward-cancel-btn">
                  {t("steward.cancel")}
                </Btn>
                <Btn variant="amber" onClick={() => onSign(pin)} disabled={!pin || loading} testid="steward-sign-btn">
                  {loading ? t("steward.signing") : t("steward.sign")}
                </Btn>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
